/**
 * Pure parsing/validation helpers for raw Keycloak response bodies and JWT
 * claims. Split out of `keycloakService.ts` (which was already over the
 * repo's 500-line hard limit before this file existed) — everything here is
 * side-effect-free and has no dependency on that module's session state
 * (`refreshPromise`, `loginInFlight`), so the split is a mechanical move, not
 * a behaviour change.
 */
import type {
  KeycloakTokenResponse,
  KeycloakUserInfo,
  OrganizationInfo,
} from "~/features/auth/types/keycloak";

// ---------------------------------------------------------------------------
// Response validation — guards against malformed Keycloak responses
// ---------------------------------------------------------------------------

// eslint-disable-next-line func-style
export function assertTokenResponse(data: unknown): asserts data is KeycloakTokenResponse {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid token response: expected an object");
  }

  const obj = data as Record<string, unknown>;
  if (typeof obj.access_token !== "string") {
    throw new Error("Invalid token response: missing access_token");
  }

  if (typeof obj.refresh_token !== "string") {
    throw new Error("Invalid token response: missing refresh_token");
  }

  if (typeof obj.expires_in !== "number") {
    throw new Error("Invalid token response: missing or invalid expires_in");
  }
}

// eslint-disable-next-line func-style
export function assertUserInfoResponse(data: unknown): asserts data is KeycloakUserInfo {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid userinfo response: expected an object");
  }

  const obj = data as Record<string, unknown>;
  if (typeof obj.sub !== "string") {
    throw new Error("Invalid userinfo response: missing sub");
  }

  if (typeof obj.email !== "string") {
    throw new Error("Invalid userinfo response: missing email");
  }
}

// ---------------------------------------------------------------------------
// Organization-claim parsing
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const parseOrganizations = (orgClaim: unknown): OrganizationInfo[] => {
  const organizations: OrganizationInfo[] = [];

  if (!orgClaim) {
    return organizations;
  }

  const addOrg = (key: string, data: Record<string, unknown> | null) => {
    const firstCountry = Array.isArray(data?.country) ? data.country[0] : undefined;
    const country = typeof firstCountry === "string" ? firstCountry : undefined;

    organizations.push({
      name: key,
      tenantId: key,
      orgId: typeof data?.id === "string" ? data.id : key,
      country,
    });
  };

  const processEntry = (entry: unknown) => {
    if (typeof entry === "string") {
      addOrg(entry, null);
    } else if (isRecord(entry)) {
      for (const [key, data] of Object.entries(entry)) {
        addOrg(key, isRecord(data) ? data : null);
      }
    }
  };

  if (Array.isArray(orgClaim)) {
    orgClaim.forEach(processEntry);
  } else if (typeof orgClaim === "string") {
    addOrg(orgClaim, null);
  } else if (isRecord(orgClaim)) {
    for (const [key, data] of Object.entries(orgClaim)) {
      addOrg(key, isRecord(data) ? data : null);
    }
  }

  return organizations.filter(
    (org, index, self) => index === self.findIndex((o) => o.tenantId === org.tenantId),
  );
};
